import {
  KAI_TABLE_PREFS_EVENT,
  loadStoredPreferences,
  mergeContentDisplay,
  normalizeContentDisplay,
  normalizePreferences,
  persistPreferences,
  type KaiTableContext,
  type KaiTablePrefsEventDetail,
  type KaiTablePrefsPatch,
  type TableColumnDisplay,
  type TablePreferences,
} from "@/components/table";
import { pinContentDisplay } from "@/components/table/tablePreferences";

import { BOOKING_TABLE_COLUMN_META } from "./bookingTableColumns";
import { BOOKING_GROUP_BY_VALUES } from "./bookingTableGroups";
import {
  BOOKING_REPORT_SYNC_COLUMN_IDS,
  BOOKING_TABLE_ONLY_COLUMN_IDS,
} from "./report/bookingLabels";

export const BOOKINGS_TABLE_STORAGE_KEY = "twido-v2-bookings-table";

const BOOKING_COLUMN_IDS = BOOKING_TABLE_COLUMN_META.map((column) => column.id);

const BOOKING_TABLE_DEFAULTS = {
  allowedGroupBy: BOOKING_GROUP_BY_VALUES,
  stickyColumns: { first: 1, last: 0 },
  showSummaryRow: true,
};

export function loadBookingsTablePrefs(): TablePreferences {
  return normalizePreferences(
    loadStoredPreferences(BOOKINGS_TABLE_STORAGE_KEY),
    BOOKING_TABLE_COLUMN_META,
    BOOKING_TABLE_DEFAULTS,
  );
}

const TABLE_ONLY_COLUMN_IDS = new Set<string>(BOOKING_TABLE_ONLY_COLUMN_IDS);
const REPORT_SYNC_COLUMN_IDS = new Set<string>(BOOKING_REPORT_SYNC_COLUMN_IDS);

/**
 * Merge a booking report’s `report_columns` deep link into stored table prefs:
 * report-syncable columns get the report’s visibility and order; table-only
 * columns (not supported in report `columns`) keep the user’s saved prefs.
 */
export function mergeReportViewIntoContentDisplay(
  stored: readonly TableColumnDisplay[],
  reportColumns: readonly string[],
  columns = BOOKING_TABLE_COLUMN_META,
): TableColumnDisplay[] {
  const normalized = normalizeContentDisplay(stored, columns);
  if (reportColumns.length === 0) {
    return normalized;
  }
  const metaById = new Map(columns.map((column) => [column.id, column]));

  const reportOrder = reportColumns.filter(
    (id) => REPORT_SYNC_COLUMN_IDS.has(id) && metaById.has(id),
  );
  const reportVisible = new Set(reportOrder);

  const byId = new Map(
    normalized.map((item) => {
      const column = metaById.get(item.id);
      if (!column || column.alwaysVisible) {
        return [item.id, { ...item, visible: true }] as const;
      }
      if (TABLE_ONLY_COLUMN_IDS.has(item.id)) {
        return [item.id, { ...item }] as const;
      }
      if (REPORT_SYNC_COLUMN_IDS.has(item.id)) {
        return [
          item.id,
          { ...item, visible: reportVisible.has(item.id) },
        ] as const;
      }
      return [item.id, { ...item }] as const;
    }),
  );

  const orderedIds: string[] = [];
  const seen = new Set<string>();

  for (const id of reportOrder) {
    if (seen.has(id)) continue;
    orderedIds.push(id);
    seen.add(id);
  }

  for (const item of normalized) {
    if (!TABLE_ONLY_COLUMN_IDS.has(item.id) || seen.has(item.id)) continue;
    orderedIds.push(item.id);
    seen.add(item.id);
  }

  for (const id of BOOKING_REPORT_SYNC_COLUMN_IDS) {
    if (seen.has(id)) continue;
    orderedIds.push(id);
    seen.add(id);
  }

  for (const item of normalized) {
    if (item.id === "actions" || seen.has(item.id)) continue;
    orderedIds.push(item.id);
    seen.add(item.id);
  }

  const reordered = orderedIds
    .map((id) => byId.get(id))
    .filter((item): item is TableColumnDisplay => item != null);

  const actions = byId.get("actions") ?? { id: "actions", visible: true };
  if (!seen.has("actions")) {
    reordered.push(actions);
  }

  return pinContentDisplay(reordered, columns);
}

/** Persist report view layout (columns + optional group-by) onto bookings table prefs. */
export function applyReportViewBookingsTablePrefs(options: {
  reportColumns: readonly string[];
  groupBy?: string;
}): TablePreferences {
  const current = loadBookingsTablePrefs();
  const contentDisplay = mergeReportViewIntoContentDisplay(
    current.contentDisplay,
    options.reportColumns,
  );

  const groupBy =
    options.groupBy !== undefined &&
    BOOKING_GROUP_BY_VALUES.includes(options.groupBy)
      ? options.groupBy
      : current.groupBy;

  const next = normalizePreferences(
    {
      ...current,
      contentDisplay,
      groupBy,
    },
    BOOKING_TABLE_COLUMN_META,
    BOOKING_TABLE_DEFAULTS,
  );

  persistPreferences(BOOKINGS_TABLE_STORAGE_KEY, next);
  window.dispatchEvent(
    new CustomEvent<KaiTablePrefsEventDetail>(KAI_TABLE_PREFS_EVENT, {
      detail: {
        page: "bookings",
        storageKey: BOOKINGS_TABLE_STORAGE_KEY,
        prefs: next,
      },
    }),
  );
  return next;
}

/** Snapshot of the current bookings table layout for Kai's client context. */
export function buildBookingsTableContextSnapshot(
  prefs: TablePreferences = loadBookingsTablePrefs(),
): KaiTableContext {
  const dataColumns = prefs.contentDisplay.filter(
    (column) => column.id !== "actions",
  );
  return {
    page: "bookings",
    visible_columns: dataColumns
      .filter((column) => column.visible)
      .map((column) => column.id),
    hidden_columns: dataColumns
      .filter((column) => !column.visible)
      .map((column) => column.id),
    column_order: dataColumns.map((column) => column.id),
    page_size: prefs.pageSize,
    content_density: "comfortable",
    sticky_columns: prefs.stickyColumns,
    group_by: prefs.groupBy || undefined,
  };
}

/**
 * Merge a Kai patch into stored bookings prefs, persist, and notify any
 * mounted table via a window event.
 */
export function applyKaiBookingsTablePrefs(
  patch: KaiTablePrefsPatch,
): TablePreferences {
  const current = loadBookingsTablePrefs();
  const contentDisplay = mergeContentDisplay(
    current.contentDisplay,
    patch,
    BOOKING_COLUMN_IDS,
  );

  const groupBy =
    patch.group_by !== undefined &&
    BOOKING_GROUP_BY_VALUES.includes(patch.group_by)
      ? patch.group_by
      : current.groupBy;

  const next = normalizePreferences(
    {
      pageSize: patch.page_size ?? current.pageSize,
      wrapLines: patch.wrap_lines ?? current.wrapLines,
      stripedRows: patch.striped_rows ?? current.stripedRows,
      contentDisplay,
      groupBy,
      stickyGroupHeaders:
        patch.sticky_group_headers ?? current.stickyGroupHeaders,
      stickyColumns: {
        first: patch.sticky_columns?.first ?? current.stickyColumns.first,
        last: patch.sticky_columns?.last ?? current.stickyColumns.last,
      },
      showSummaryRow: patch.show_summary_row ?? current.showSummaryRow,
      resizableColumns: patch.resizable_columns ?? current.resizableColumns,
    },
    BOOKING_TABLE_COLUMN_META,
    BOOKING_TABLE_DEFAULTS,
  );

  persistPreferences(BOOKINGS_TABLE_STORAGE_KEY, next);
  window.dispatchEvent(
    new CustomEvent<KaiTablePrefsEventDetail>(KAI_TABLE_PREFS_EVENT, {
      detail: {
        page: "bookings",
        storageKey: BOOKINGS_TABLE_STORAGE_KEY,
        prefs: next,
      },
    }),
  );
  return next;
}
